// RequireRole — role-gated route subtree.
//
// Used to gate the `/admin/*` subtree to tenant_admin (and optionally
// platform_admin acting via X-Act-As-Tenant impersonation, Story 205).
//
// Behaviour:
//   - On mount, fires a fresh `/api/auth/v1/me` GET to learn the current
//     user's roles. The session-blob initial roles can be stale (e.g.
//     after a BE-side promotion), so RequireRole refuses to take the
//     stored roles as load-bearing. While the fetch is pending the
//     children render optimistically — the underlying RequireAuth gate
//     has already proven the session.
//   - If the user's roles include any of the required roles, render
//     `<Outlet/>` (or children when used as a wrapper).
//   - Otherwise, render the supplied `fallback` (a 403 page by default).
//
// Story 053 — added the role gate for `/admin/*`.
import { useEffect, useState, type ReactElement, type ReactNode } from "react";
import { Outlet } from "react-router-dom";
import { useAuthStore } from "./store.js";
import { getAuthBaseUrl } from "./config.js";
import type { Me, Role } from "./types.js";

export interface RequireRoleProps {
  /** One or more roles that grant access. Any-match wins. */
  role: Role | readonly Role[];
  /** Rendered when the user lacks the required role. */
  fallback: ReactElement;
  /**
   * Optional explicit children. If absent the component renders an
   * `<Outlet/>` so it can be used as a route-element wrapper.
   */
  children?: ReactNode;
}

interface RoleFetchState {
  status: "pending" | "ok" | "error";
  roles: readonly Role[];
}

export function RequireRole({
  role,
  fallback,
  children,
}: RequireRoleProps): ReactElement {
  const session = useAuthStore((s) => s.session);
  const tenant = useAuthStore((s) => s.tenant);
  const accessToken = session?.accessToken;
  const tenantSlug = tenant?.tenantSlug;
  const sessionRoles: readonly Role[] = session?.user.roles ?? [];

  const [state, setState] = useState<RoleFetchState>({
    status: "pending",
    roles: sessionRoles,
  });

  useEffect(() => {
    let cancelled = false;
    if (!accessToken || !tenantSlug) {
      return;
    }
    fetch(`${getAuthBaseUrl()}/me`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "X-Tenant-ID": tenantSlug,
      },
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(`/me ${String(res.status)}`);
        return (await res.json()) as Me;
      })
      .then((me) => {
        if (cancelled) return;
        // The `as Me` cast above is a compile-time claim, not a runtime
        // guarantee: a partial or malformed `/me` body still parses as JSON
        // and sails through it. Without this check a missing `roles` array
        // reaches `state.roles.includes(...)` below and throws, which takes
        // down the whole `/admin/*` subtree with a blank screen. Treat a
        // payload we can't read as a failed fetch and use the session roles.
        if (!Array.isArray(me.roles)) {
          setState({ status: "error", roles: sessionRoles });
          return;
        }
        setState({ status: "ok", roles: me.roles });
      })
      .catch(() => {
        if (cancelled) return;
        // On error, fall back to session-blob roles. This keeps the
        // optimistic-render path consistent if the BE is temporarily
        // unreachable.
        setState({ status: "error", roles: sessionRoles });
      });
    return () => {
      cancelled = true;
    };
    // sessionRoles is derived from session.user; we re-fetch when
    // accessToken/tenantSlug change. Adding sessionRoles to deps would
    // cause an infinite refetch when /me updates the auth store.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, tenantSlug]);

  const required: readonly Role[] = Array.isArray(role)
    ? (role as readonly Role[])
    : [role as Role];
  const hasAny = required.some((r: Role) => state.roles.includes(r));

  if (state.status === "pending") {
    // Optimistic render while /me resolves. Most tenants won't notice
    // the brief flicker; analysts who hit /admin URLs directly land on
    // the page until the role check completes, then see the fallback.
    if (
      sessionRoles.length > 0 &&
      required.some((r) => sessionRoles.includes(r))
    ) {
      return <>{children ?? <Outlet />}</>;
    }
    // No optimistic match — render the fallback while we resolve.
    return fallback;
  }

  if (!hasAny) {
    return fallback;
  }
  return <>{children ?? <Outlet />}</>;
}
